import {
  SKY_CATHEDRAL_COURT,
  SKY_CATHEDRAL_SPACE,
} from '../../../packages/domain/src/99-sky-cathedral-space.mjs';
import { skyFlightPointToWorld } from '../../../packages/domain/src/92-sky-flight-frame.mjs';

/** Production support surface, authored in the same metres as99's collisions.
 * Original ImageGen stone texels cover the court; source-backed boss geometry
 * owns the six legs. Historical unrendered rooftop boxes are never included.
 * The caller owns stoneMap; this adapter owns its private bump view and meshes. */
export function createSkyCathedralCourt(T, stoneMap) {
  const group = new T.Group();
  group.name = 'Flooded Cathedral foundation';
  group.visible = false;
  const resources = new Set();
  const own = (value) => {
    resources.add(value);
    return value;
  };
  const bump = own(stoneMap.clone());
  bump.colorSpace = T.NoColorSpace;
  const material = own(
    new T.MeshStandardMaterial({
      map: stoneMap,
      bumpMap: bump,
      bumpScale: 0.12,
      color: '#70858a',
      roughness: 0.72,
      metalness: 0.08,
      side: T.DoubleSide,
    }),
  );
  for (const part of SKY_CATHEDRAL_COURT) {
    const positions = [],
      uv = [];
    for (const triangle of part.triangles) {
      const points = ['a', 'b', 'c'].map((key) => skyFlightPointToWorld(part.frame, triangle[key]));
      const a = new T.Vector3().subVectors(points[1], points[0]);
      const b = new T.Vector3().subVectors(points[2], points[0]);
      const normal = a.cross(b);
      const top = Math.abs(normal.y) > Math.max(Math.abs(normal.x), Math.abs(normal.z));
      const side = Math.abs(normal.x) > Math.abs(normal.z);
      for (const p of points) {
        positions.push(p.x, p.y, p.z);
        uv.push((top || !side ? p.x : p.z) / 6, (top ? p.z : p.y) / 6);
      }
    }
    const geometry = own(new T.BufferGeometry());
    geometry.setAttribute('position', new T.Float32BufferAttribute(positions, 3));
    geometry.setAttribute('uv', new T.Float32BufferAttribute(uv, 2));
    geometry.computeVertexNormals();
    const mesh = new T.Mesh(geometry, material);
    mesh.name = part.id;
    mesh.receiveShadow = true;
    group.add(mesh);
  }
  const water = new T.Mesh(
    own(new T.PlaneGeometry(500, 500)),
    own(
      new T.MeshPhysicalMaterial({
        color: '#24414c',
        roughness: 0.3,
        metalness: 0.24,
        clearcoat: 0.65,
        clearcoatRoughness: 0.22,
        side: T.DoubleSide,
      }),
    ),
  );
  water.name = 'Floodwater at canonical drowning surface';
  water.rotation.x = -Math.PI / 2;
  water.position.y = SKY_CATHEDRAL_SPACE.waterY;
  group.add(water);
  let disposed = false;
  return {
    group,
    draw(state) {
      group.visible =
        !disposed && Boolean(state.cathedral?.pose?.artwork && state.cathedral.pose.valid);
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      group.removeFromParent();
      for (const resource of resources) resource.dispose();
      resources.clear();
    },
  };
}
